import { StickyNote } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useSetNote } from "@/hooks/use-machines";
import type { Machine } from "@/lib/types";
import { cn } from "@/lib/utils";

const NOTE_MAX_LENGTH = 2000;

/**
 * Edit (or clear) the holder note on one or more machines. Used for both the
 * per-row inline edit (single machine, prefilled with the current note) and the
 * bulk toolbar action (many machines, blank — the same text is applied to all).
 */
export function NoteDialog({
  machines,
  open,
  onOpenChange,
  onDone,
}: {
  machines: Machine[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone?: () => void;
}) {
  const setNote = useSetNote();
  // Single-machine edits prefill with the current note; bulk starts blank.
  const seed = machines.length === 1 ? (machines[0]?.note ?? "") : "";
  const [text, setText] = useState(seed);

  // Re-seed the textarea from the current note each time the dialog opens.
  useEffect(() => {
    if (open) setText(seed);
  }, [open, seed]);

  if (machines.length === 0) return null;

  const isBulk = machines.length > 1;
  const title = isBulk
    ? `Note for ${machines.length} machines`
    : `Note for ${machines[0]?.name ?? "machine"}`;

  const submit = () => {
    const trimmed = text.trim();
    setNote.mutate(
      {
        machineIds: machines.map((machine) => machine.id),
        note: trimmed.length > 0 ? trimmed : null,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          onDone?.();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {isBulk
              ? "The same note is applied to every selected machine. Visible to everyone; cleared when the machine is released."
              : "Visible to everyone; cleared automatically when the machine is released."}
          </DialogDescription>
        </DialogHeader>
        <textarea
          autoFocus
          value={text}
          maxLength={NOTE_MAX_LENGTH}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === "Enter") submit();
          }}
          placeholder="e.g. running llama70b benchmark, don't kick"
          rows={4}
          className={cn(
            "w-full resize-none rounded-lg border border-input bg-transparent px-2.5 py-1.5 text-sm outline-none transition-colors",
            "placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
            "dark:bg-input/30",
          )}
        />
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={setNote.isPending}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={setNote.isPending}>
            {text.trim().length === 0 ? "Clear note" : "Save note"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Holder note shown inline next to a machine's IP in the table: the note text
 * itself, single-line and ellipsized to fit the column, with the full text in
 * a tooltip. Holders and admins get click-to-edit, plus a dim sticky-note icon
 * as an add affordance when there is no note yet. Read-only viewers see
 * nothing when there is no note.
 */
export function NoteButton({ machine, canEdit }: { machine: Machine; canEdit?: boolean }) {
  const [open, setOpen] = useState(false);
  // Treat an empty/whitespace note the same as no note.
  const note = machine.note?.trim() || null;

  // Nothing to show and nothing to add: render nothing so the IP line stays clean.
  if (!canEdit && !note) return null;

  // Read-only with a note: non-interactive text with the full note on hover.
  if (!canEdit) {
    return (
      <Tooltip>
        <TooltipTrigger
          render={<span />}
          className="min-w-0 cursor-default truncate text-[11px] text-amber-600 dark:text-amber-400"
        >
          {note}
        </TooltipTrigger>
        <TooltipContent className="max-w-xs whitespace-pre-wrap">{note}</TooltipContent>
      </Tooltip>
    );
  }

  // Holder/admin: clicking the note text opens the editor; tooltip shows the
  // full note. Without a note, a dim sticky-note icon is the add affordance.
  return (
    <>
      <Tooltip>
        <TooltipTrigger
          type="button"
          onClick={() => setOpen(true)}
          aria-label={note ? undefined : "Add note"}
          className={cn(
            "rounded transition-colors hover:bg-accent",
            note
              ? "min-w-0 truncate px-0.5 text-left text-[11px] text-amber-600 dark:text-amber-400"
              : "inline-flex shrink-0 items-center p-0.5 text-muted-foreground/40 hover:text-muted-foreground",
          )}
        >
          {note ?? <StickyNote className="h-3 w-3 shrink-0" />}
        </TooltipTrigger>
        <TooltipContent className="max-w-xs whitespace-pre-wrap">
          {note ?? "Add note"}
        </TooltipContent>
      </Tooltip>
      <NoteDialog machines={[machine]} open={open} onOpenChange={setOpen} />
    </>
  );
}
